import React, { useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled, { useTheme } from 'styled-components';

import { colors } from '../../../core';
import Icon from '../Icon';
import Modal from '../Modal';
import Text from '../Text';
import PDFCard from './PDFCard';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PageNums = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 0 24px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DocumentWrapper = styled.div`
  canvas {
    width: 100% !important;
    height: auto !important;
  }
`;

const ControlButtons = styled.button<{ disabled: boolean }>`
  display: flex;
  -webkit-appearance: none;
  border: 0;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.pdfControlsColor || colors.primary};

  ${({ disabled, theme }) =>
    disabled &&
    `
      cursor: default;
      background-color: ${theme.pdfControlsDisabledColor || colors.primaryDisabled}
    `}
`;

interface PDFProps {
  pdf: string;
  title: string;
  onDownload?: () => Promise<boolean> | void;
  renderMode?: 'canvas' | 'svg' | 'none' | undefined;
  onFullSize?: () => void;
  onNewTab?: () => void;
  onPrint?: () => void;
  renderTextLayer?: boolean;
  showPageButtons?: boolean;
  expanded?: boolean;
  width?: number;
  backdropMode?: 'light' | 'dark';
}

// this is necessary for gatsby to build using the window object
const isBrowser = typeof window !== 'undefined';

const PDF = ({
  pdf,
  title,
  renderMode = 'svg',
  onDownload,
  onFullSize,
  onNewTab,
  onPrint,
  renderTextLayer = true,
  showPageButtons = true,
  expanded = false,
}: PDFProps) => {
  const ref = useRef<any>();
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const theme = useTheme();

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const changePage = (offset: number) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  const previousPage = () => {
    changePage(-1);
  };

  const nextPage = () => {
    changePage(1);
  };

  return (
    <PDFCard
      title={title}
      onDownload={onDownload}
      onFullSize={onFullSize}
      onNewTab={onNewTab}
      onPrint={onPrint}
      expanded={expanded}
    >
      <Wrapper ref={ref}>
        {numPages && showPageButtons ? (
          <ControlButtons type="button" disabled={pageNumber <= 1} onClick={previousPage}>
            <Icon
              type={'caret-left'}
              color={theme.pdfControlsIconColor || colors.white}
              size={28}
            />
          </ControlButtons>
        ) : (
          <div />
        )}
        <DocumentWrapper>
          <Document file={pdf} onLoadSuccess={onDocumentLoadSuccess} renderMode={renderMode}>
            {showPageButtons ? (
              <Page
                pageNumber={pageNumber}
                renderTextLayer={renderTextLayer}
                width={ref?.current?.offsetWidth}
                scale={ref?.current?.offsetWidth ? 0.9 : 1.0}
              />
            ) : (
              Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  renderTextLayer={renderTextLayer}
                  width={ref?.current?.offsetWidth}
                  scale={ref?.current?.offsetWidth ? 0.9 : 1.0}
                />
              ))
            )}
          </Document>
        </DocumentWrapper>
        {numPages && showPageButtons ? (
          <ControlButtons type="button" disabled={pageNumber >= numPages} onClick={nextPage}>
            <Icon
              type={'caret-right'}
              color={theme.pdfControlsIconColor || colors.white}
              size={28}
            />
          </ControlButtons>
        ) : (
          <div />
        )}
      </Wrapper>
      {numPages && showPageButtons ? (
        <PageNums>
          <Text>
            Page {pageNumber} of {numPages}
          </Text>
        </PageNums>
      ) : (
        <div />
      )}
    </PDFCard>
  );
};

const PDFWrapper = ({
  pdf,
  title,
  renderMode = 'canvas',
  onDownload,
  onNewTab,
  renderTextLayer = true,
  showPageButtons = true,
  backdropMode = 'light',
}: PDFProps) => {
  const [showFullSize, setShowFullSize] = useState(false);
  const onFullSize = () => setShowFullSize(true);

  const base64ToBlob = (base64: string) => {
    const binStr = atob(base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type: 'application/pdf' });
  };

  /**
   * Load the PDF in new tab from a blob:URI instead of from a data:URI to avoid the bug
   * where PDF size greater than the browser's limitation
   * ref: https://stackoverflow.com/questions/58488416/open-base64-encoded-pdf-file-using-javascript-issue-with-file-size-larger-than/60256297#60256297
   */
  const onPrint = async () => {
    const pdfWindow = isBrowser && window.open('');

    if (pdf && pdfWindow) {
      let blob;

      if (pdf.startsWith('data:application/pdf;base64,')) {
        // If pdf is a base64-encoded string
        const base64Data = pdf.replace('data:application/pdf;base64,', '');
        blob = base64ToBlob(base64Data);
      } else {
        // If pdf is a URL
        try {
          const response = await fetch(pdf);
          const arrayBuffer = await response.arrayBuffer();
          blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        } catch (error) {
          console.error('Error fetching PDF from URL:', error);
          return;
        }
      }

      pdfWindow.document.write(
        '<iframe src="' +
          URL.createObjectURL(blob) +
          '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>'
      );
    }
  };

  return (
    <React.Fragment>
      {showFullSize ? (
        <Modal width={'90%'} onClose={() => setShowFullSize(false)} backdropMode={backdropMode}>
          <PDF
            pdf={pdf}
            title={title}
            onDownload={onDownload}
            onFullSize={() => setShowFullSize(false)}
            onNewTab={onNewTab}
            onPrint={onPrint}
            renderTextLayer={renderTextLayer}
            showPageButtons={showPageButtons}
            expanded
            renderMode={renderMode}
          />
        </Modal>
      ) : (
        <PDF
          pdf={pdf}
          title={title}
          onDownload={onDownload}
          onFullSize={onFullSize}
          onNewTab={onNewTab}
          onPrint={onPrint}
          renderTextLayer={renderTextLayer}
          showPageButtons={showPageButtons}
          expanded={false}
          renderMode={renderMode}
        />
      )}
    </React.Fragment>
  );
};

export default PDFWrapper;
